import React, { useEffect, useMemo, useState } from 'react'
import { Card, CardBody, Container, Alert, Spinner, Row, Col, Input, Label } from 'reactstrap'
import DataTable, { TableColumn } from 'react-data-table-component'
import Loader from '../../../Components/Widgets/Loader/generic'
import { get } from '../../../hook/request'
import { fmtDateTime } from '../../../Helpers/index'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

type CronologiaRow = {
  ID: number
  codice: string
  bolla?: string | null
  data_ins: string
  nominativo: string
  esito: number
  text?: string | null
  nome_modello: string | null
  variante: string | null
  taglia: string | null
  statusID: number
}

function unwrapPayload(res: any) {
  return res?.data?.data ?? res?.data ?? res
}

export default function ScannerCronologia() {
  const [rows, setRows] = useState<CronologiaRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filtroDataStart, setFiltroDataStart] = useState('')
  const [filtroDataEnd, setFiltroDataEnd] = useState('')
  const [filtroModel, setFiltroModel] = useState('')
  const [filtroStatusID, setFiltroStatusID] = useState('')

  const load = async (filters?: { dataStart?: string; dataEnd?: string; model?: string; statusID?: string }) => {
    setError(null)
    setLoading(true)
    try {
      const dataStartParam = filters?.dataStart ?? filtroDataStart
      const dataEndParam = filters?.dataEnd ?? filtroDataEnd
      const modelParam = filters?.model ?? filtroModel.trim()
      const statusIDParam = filters?.statusID ?? filtroStatusID
      const res = await get<any>('/scanner/cronologia', {
        dataStart: dataStartParam,
        dataEnd: dataEndParam,
        model: modelParam,
        statusID: statusIDParam,
      })
      const payload = unwrapPayload(res)

      const status = String(payload?.status ?? '').toLowerCase()
      const list = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.list)
        ? payload.list
        : Array.isArray(payload)
        ? payload
        : []

      if (status === 'ok' || Array.isArray(list)) {
        setRows(list as CronologiaRow[])
      } else {
        setRows([])
        setError(String(payload?.message ?? 'Operazione non riuscita').trim())
      }
    } catch (e: any) {
      const payload = unwrapPayload(e?.response)
      setRows([])
      setError(String(payload?.message ?? e?.message ?? 'Errore di rete').trim())
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const handlePrintPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })

    const titolo = 'Cronologia Scanner'
    const filtri = ''

    doc.setFontSize(14)
    doc.text(titolo, 40, 40)

    doc.setFontSize(10)
    doc.text(filtri, 40, 60)

    const head = [['Codice', 'Modello', 'Bolla', 'Utente', 'Eseguito il', 'Messaggio']]
    const body = rows.map(r => [
      r.codice ?? '-',
      r.nome_modello ? `${r.nome_modello} - ${r.variante ?? ''}-${r.taglia ?? ''}` : '-',
      r.bolla ?? '',
      r.nominativo ?? '-',
      fmtDateTime(r.data_ins),
      (r.text ?? '').toString(),
    ])

    autoTable(doc, {
      head,
      body,
      startY: 80,
      styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
      headStyles: { fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 180 },
        2: { cellWidth: 110 },
        3: { cellWidth: 120 },
        4: { cellWidth: 110 },
        5: { cellWidth: 260 },
      },
      didDrawPage: () => {
        const pageSize = doc.internal.pageSize
        const pageHeight = pageSize.height ? pageSize.height : (pageSize as any).getHeight()
        const pageWidth = pageSize.width ? pageSize.width : (pageSize as any).getWidth()
        doc.setFontSize(9)
        doc.text(`Pagina ${doc.getNumberOfPages()}`, pageWidth - 80, pageHeight - 20)
      },
    })

    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
    doc.save(`cronologia-scanner-${ts}.pdf`)
  }

  const resetFiltri = () => {
    setFiltroDataStart('')
    setFiltroDataEnd('')
    setFiltroModel('')
    setFiltroStatusID('')
    load({ dataStart: '', dataEnd: '', model: '', statusID: '' })
  }

  const columns: TableColumn<CronologiaRow>[] = useMemo(
    () => [
      { name: 'Codice', selector: r => r.codice ?? '-', sortable: true, width: '150px' },
      { name: 'Modello', selector: r => (r.nome_modello ? r.nome_modello + ' - ' + r.variante + '-' + r.taglia : '-'), sortable: true, minWidth: '160px' },
      { name: 'Bolla', selector: r => r.bolla ?? ' ', sortable: true, width: '100px' },
      { name: 'Utente', selector: r => r.nominativo ?? '-', sortable: true, width: '160px' },
      { name: 'Eseguito il', selector: r => fmtDateTime(r.data_ins), sortable: true, minWidth: '200px' },
      { name: 'Messaggio', selector: r => ( r.statusID != 1 ? (r.text ?? '') : '').toString(), sortable: false, grow: 2 },
    ],
    []
  )

  return (
    <Container fluid className="p-3">
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h3 className="mb-1">Cronologia Scanner</h3>
              <small className="text-muted">Ultime scansioni effettuate.</small>
            </div>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => load()}
                disabled={loading}
              >
                {loading ? <Spinner size="sm" /> : 'Aggiorna'}
              </button>

              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handlePrintPdf}
                disabled={loading || rows.length === 0}
              >
                Stampa
              </button>
            </div>
          </div>

          <Row className="mt-3 g-2 align-items-end cron-filter">
            <Col md={2}>
              <Label htmlFor="filtro-data-start" className="mb-1">Da</Label>
              <Input
                id="filtro-data-start"
                type="date"
                value={filtroDataStart}
                onChange={e => setFiltroDataStart(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label htmlFor="filtro-data-end" className="mb-1">A</Label>
              <Input
                id="filtro-data-end"
                type="date"
                value={filtroDataEnd}
                onChange={e => setFiltroDataEnd(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Label htmlFor="filtro-model" className="mb-1">Modello</Label>
              <Input
                id="filtro-model"
                type="text"
                placeholder="Nome modello..."
                value={filtroModel}
                onChange={e => setFiltroModel(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label htmlFor="filtro-status-id" className="mb-1">Stato</Label>
              <Input
                id="filtro-status-id"
                type="select"
                value={filtroStatusID}
                onChange={e => setFiltroStatusID(e.target.value)}
              >
                <option value="">Tutti</option>
                <option value="-1">Errori</option>
                <option value="1">Codici trovati</option>
                <option value="2">Quantita superata</option>
                <option value="3">Inseriti Manualmente</option>
              </Input>
            </Col>
            <Col md={3} className="d-flex gap-2 mt-0">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => load()}
                disabled={loading}
              >
                Filtra
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={resetFiltri}
                disabled={loading}
              >
                Reset
              </button>
            </Col>
          </Row>

          <div className="mt-3">
            {error && (
              <Alert color="danger" className="mb-3">
                {error}
              </Alert>
            )}

            {!loading && rows.length === 0 && !error && (
              <Alert color="warning" className="mb-0">
                Nessun dato in cronologia.
              </Alert>
            )}

            <DataTable<CronologiaRow>
              data={rows}
              columns={columns}
              keyField="ID"
              progressPending={loading}
              progressComponent={<Loader />}
              pagination
              paginationPerPage={50}
              paginationRowsPerPageOptions={[50, 100, 200]}
              striped
              highlightOnHover
              className="display"
              conditionalRowStyles={[
                {
                  when: row => Number(row.esito) === 1,
                  style: { backgroundColor: 'rgba(21, 219, 68, 0.22)' },
                },
                {
                  when: row => Number(row.esito) !== 1,
                  style: { backgroundColor: 'rgba(220, 53, 70, 0.22)' },
                },
              ]}
              noDataComponent={loading ? 'Caricamento...' : 'Nessun dato.'}
            />
          </div>
        </CardBody>
      </Card>
    </Container>
  )
}
